import { inject, Injectable } from '@angular/core';
import { Dictionary, EntityState } from '@ngrx/entity';
import { Action, Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  map,
  Observable,
  of,
} from 'rxjs';
import { PartialDeep } from 'type-fest';

import {
  CanvasElement,
  CanvasElementFrom,
  CanvasElementId,
  CanvasElementType,
  CanvasNamespaceId,
  ElementTargetPath,
  LayoutData,
  PropsOf,
  SlotId,
} from '@blaze-runtime/engine-domain';
import {
  CanvasElementInputBinding,
  DataBindingSourceInfo,
  DataBindingSourceType,
  getActiveBinding,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  ApiCallSchema,
  BaseSchema,
  DatasetColumn,
  DatasetSchema,
  WorkflowSchema,
} from '@blaze-shared/schema-domain';
import {
  findFirstNodeInWorkflowOfType,
  isApiCallNode,
  isGetNode,
  Workflow,
} from '@blaze-shared/workflow-domain';

import { CanvasElementDefinitionInternal } from '../../canvas-elements/element-definition';
import { findWorkflowSourceNode } from '../../canvas-elements/element-model/data-binding';
import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import { isElementType } from '../../canvas-elements/element-model/element-type-guards';
import { ElementDefinitionsService } from '../../services/element-definitions.service';
import { ElementStoreViewService } from '../../services/element-store-view.service';
import {
  placeElement,
  PlaceElementResult,
  PlaceElementStyleData,
} from '../../utils/layout/place-element';
import { elementsInternal } from '../elements-internal/elements-internal.actions';
import { getEngineState } from '../engine/engine-adapters';
import { EngineState } from '../engine/engine.model';

import { navigateToNode, updateBinding } from './elements.actions';
import {
  getElementIds,
  getElements,
  getLayoutEntities,
  getLayoutIds,
  getOffSyncLayouts,
  getOverlayIds,
  getSelectedPathNode,
} from './elements.selectors';

export interface SelectedPathData {
  element?: CanvasElement;
  slotId?: SlotId;
}

export interface ElementsChangeEvent<T extends CanvasElementType> {
  type: ElementsChangeEventType;
  element: CanvasElementFrom<T>;
}

export type ElementsChangeEventType = 'init' | 'changed' | 'removed';

// TODO: test
@Injectable({
  providedIn: 'root',
})
export class ElementsFacade {
  private readonly store = inject(Store<EngineState>);

  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly schemaService = inject(SchemaService);

  private readonly elementStoreView = inject(ElementStoreViewService);
  readonly ids$: Observable<CanvasElementId[]>;
  readonly entities$: Observable<Dictionary<CanvasElement>>;
  readonly entityState$: Observable<EntityState<CanvasElement>>;
  readonly elements$: Observable<CanvasElement[]>;
  readonly overlayIds$ = this.store.select(getOverlayIds);
  readonly overlayOpened$ = this.overlayIds$.pipe(
    map((overlayIds) => overlayIds.length > 0),
    distinctUntilChanged(),
  );

  readonly layoutIds$: Observable<CanvasNamespaceId[]>;
  readonly layoutEntities$: Observable<Record<CanvasNamespaceId, LayoutData>>;
  readonly offSyncLayouts$: Observable<Record<CanvasNamespaceId, boolean>>;

  constructor() {
    this.ids$ = this.store.select(getElementIds);
    this.entities$ = this.elementStoreView.entities$;
    this.entityState$ = this.elementStoreView.entityState$;
    this.elements$ = this.store.select(getElements);

    this.layoutIds$ = this.store.select(getLayoutIds);
    this.layoutEntities$ = this.store.select(getLayoutEntities);
    this.offSyncLayouts$ = this.store.select(getOffSyncLayouts);
  }

  getSelected(): Observable<SelectedPathData | undefined> {
    return combineLatest({
      entities: this.entities$,
      selectedPathNode: this.store.select(getSelectedPathNode),
    }).pipe(
      map(({ entities, selectedPathNode }) => {
        if (!selectedPathNode) {
          return;
        }

        return {
          element: entities[selectedPathNode.id],
          slotId: selectedPathNode.slot,
        };
      }),
    );
  }

  getElement(elementId: CanvasElementId): Observable<CanvasElement | undefined> {
    return this.entities$.pipe(map((entities) => entities[elementId]));
  }

  /** Adds a new element in a synchronous manner and returns the result */
  placeNewElement(
    elementDefinition: CanvasElementDefinitionInternal,
    atPath: ElementTargetPath,
    initProps?: PartialDeep<PropsOf<CanvasElementType>>,
    style?: PlaceElementStyleData,
  ): PlaceElementResult {
    let result: PlaceElementResult | undefined;
    this.store
      .select(getEngineState)
      .pipe(first())
      .subscribe((state) => {
        result = placeElement(state, { elementDefinition, atPath, initProps, style });
      });

    if (!result) {
      throw new Error('Place element error');
    }

    this.store.dispatch(elementsInternal.placeNewElement({ entityState: result.state }));
    return result;
  }

  navigateToNode(elementId: string, slotId?: SlotId): void {
    this.store.dispatch(navigateToNode({ elementId, slotId }));
  }

  watchElementsOfType<T extends CanvasElementType>(
    type: T,
  ): Observable<Observable<ElementsChangeEvent<T>>> {
    return new Observable<Observable<ElementsChangeEvent<T>>>((subscriber) => {
      const elementSubjectMap = new Map<CanvasElementId, BehaviorSubject<ElementsChangeEvent<T>>>();

      const elementSubscription = this.elements$.subscribe((elements) => {
        // Look for all elements of the provided type
        const elementsOfType = elements.filter((element) =>
          isElementType(element, type),
        ) as CanvasElementFrom<T>[];

        const initialized = elementsOfType.filter((element) => !elementSubjectMap.has(element.id));
        // No longer exists (removed or type changed)
        const removedIds: CanvasElementId[] = [];
        for (const id of elementSubjectMap.keys()) {
          if (!elementsOfType.some((el) => el.id === id)) {
            removedIds.push(id);
          }
        }

        const edited = elementsOfType.filter(
          (element) =>
            elementSubjectMap.has(element.id) &&
            element !== elementSubjectMap.get(element.id)?.value.element,
        );

        // For each initialized element - create a new source and emit it
        initialized.forEach((element) => {
          const subject = new BehaviorSubject<ElementsChangeEvent<T>>({ type: 'init', element });
          elementSubjectMap.set(element.id, subject);
          subscriber.next(subject.asObservable());
        });

        // For each edited element - emit the new value
        edited.forEach((element) =>
          elementSubjectMap.get(element.id)?.next({ type: 'changed', element }),
        );

        // For each removed element/changed type - complete the source
        removedIds.forEach((id) => {
          const subjectValue = elementSubjectMap.get(id);
          subjectValue?.next({ type: 'removed', element: subjectValue.value.element });
          subjectValue?.complete();
          elementSubjectMap.delete(id);
        });
      });

      // Finalization/cleanup
      return () => {
        elementSubjectMap.forEach((elementSubject) => elementSubject.complete());
        elementSubjectMap.clear();
        elementSubscription.unsubscribe();
      };
    });
  }

  getUpdateBindingAction(
    binding: {
      elementId: CanvasElementId;
      bindingKey: CanvasElementInputBinding;
      isNewDataSource: boolean;
      visibleFields?: string[];
    },
    entities: Dictionary<CanvasElement>,
    workflows: Workflow[],
  ): Observable<Action> {
    const { elementId, bindingKey, isNewDataSource, visibleFields } = binding;
    const element = entities[elementId];
    if (!element || !hasDataProps(element)) {
      return EMPTY;
    }

    const inputConfig = this.elementDefinitionsService.getInputConfig(element.type, bindingKey);

    let dataSource = (element.props.data || []).find(
      (data) => data?.key === bindingKey,
    )?.dataSource;
    if (!dataSource) {
      return EMPTY;
    }

    // Don't refresh other types
    const refreshedSourceTypes: DataBindingSourceType[] = [
      'api-call',
      'context',
      'data-filter',
      'dataset',
      'formGroup',
      'mirror',
    ];
    if (!refreshedSourceTypes.includes(dataSource.sourceType)) {
      return EMPTY;
    }

    if (dataSource.sourceType === 'mirror') {
      const targetElement = entities[dataSource.id];
      if (!targetElement || !hasDataProps(targetElement)) {
        throw new Error('target element missing or invalid');
      }
      dataSource = getActiveBinding(targetElement.props.data, bindingKey)?.dataSource;
      if (!dataSource) {
        throw new Error('target element datasource missing or invalid');
      }
    }

    const isWorkflow =
      dataSource.sourceType === 'context' || dataSource.sourceType === 'data-filter';
    const workflow = isWorkflow ? workflows.find(({ id }) => id === dataSource.id) : undefined;

    // Preserve columns if API Call with known result schema (this happens when source is new and columns are already prepopulated)
    let resultColumns: DatasetColumn[] | undefined;
    if (isWorkflow && dataSource.columns?.length > 0 && isNewDataSource) {
      const sourceNode = workflow && findWorkflowSourceNode(workflow);
      if (isApiCallNode(sourceNode)) {
        resultColumns = dataSource.columns;
      }
    }

    // Figure out dataset on which Filters are applied (if applicable)
    let filteredDatasetId: string | undefined;
    if (dataSource.sourceType === 'dataset') {
      filteredDatasetId = dataSource.id;
    } else if (isWorkflow) {
      filteredDatasetId = findFirstNodeInWorkflowOfType(workflow, isGetNode)?.datasetId;
    }

    const schema$: Observable<ApiCallSchema | DatasetSchema | WorkflowSchema | undefined> =
      dataSource.sourceType === 'api-call'
        ? this.getApiCallSchema(dataSource)
        : this.schemaService.obtainSchema(dataSource.id, isWorkflow);

    // Obtain latest schema - forced recalculation for workflow
    return schema$.pipe(
      filter((schema): schema is BaseSchema => !!schema),
      map((schema: WorkflowSchema | ApiCallSchema | DatasetSchema) => {
        const filteredDatasetColumns = filteredDatasetId
          ? this.schemaService.getSchema(filteredDatasetId)?.columns
          : undefined;

        return updateBinding({
          elementId: element.id,
          inputConfig,
          bindingKey,
          // If result columns known - preserve them
          schema: resultColumns ? { ...schema, columns: resultColumns } : schema,
          filteredDatasetColumns,
          isNewDataSource,
          visibleFields,
        });
      }),
    );
  }

  private getApiCallSchema(
    dataSource: DataBindingSourceInfo,
  ): Observable<ApiCallSchema | undefined> {
    if (dataSource.sourceType !== 'api-call') {
      throw new Error('Unexpected data source type');
    }

    // The data source is set to api-call - happens when the user set the API Call source option and has not yet configured it
    if (!dataSource.id || !dataSource.name) {
      return of({
        columns: [],
        id: '',
        name: '',
        supportsPagination: false,
      });
    }

    // Case where the API Call source is not configured within a workflow is not supported
    console.warn('API Call source should be used inside of a workflow');
    return EMPTY;
  }
}
